import type { ReactNode } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { formatAuditModule, useAuditRecordDetail } from "@/modules/audit";
import type { AuditRecord } from "@/modules/audit";
import { formatAuditDateTime } from "../../audit-date-time";
import styles from "./audit-detail-dialog.module.scss";

export const AUDIT_DETAIL_TITLE = "Detalhes da Auditoria";
export const AUDIT_DETAIL_LOADING_MESSAGE = "Carregando os detalhes da auditoria...";
export const AUDIT_DETAIL_RETRY_LABEL = "Tentar novamente";
export const AUDIT_DETAIL_REAUTH_LABEL = "Entrar novamente";

/** Campo obrigatório que o serviço devolveu vazio, no padrão da tela de Usuários. */
const EMPTY_FIELD = "—";

interface AuditDetailDialogProps {
  /** Identificador da linha acionada em Detalhar (RN001). */
  recordId: string;
  /** Única ação de fechamento do modal; devolve o foco à listagem (AC4). */
  onClose: () => void;
  /** Fluxo de reautenticação acionado quando a sessão expira (401). */
  onReauthenticate: () => void;
}

/** Iniciais do responsável no avatar, no mesmo formato do menu do usuário. */
function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";

  return `${first}${last}`.toUpperCase();
}

interface DetailFieldProps {
  label: string;
  testId: string;
  /**
   * Coluna do campo no card. O protótipo lê a alteração da esquerda para a
   * direita: Ação e Justificativa à esquerda, Valor Anterior e Novo Valor à
   * direita, cada par na sua linha.
   */
  column?: "left" | "right";
  children: ReactNode;
}

function DetailField({ label, testId, column = "left", children }: DetailFieldProps) {
  return (
    <div className={`${styles.field} ${column === "right" ? styles.right : styles.left}`}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.value} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

interface UserLineProps {
  /** Ícone do dado, à esquerda do valor, como no protótipo. */
  icon: string;
  label: string;
  testId: string;
  children: ReactNode;
}

/** Linha de contato do responsável: ícone à esquerda e o valor ao lado. */
function UserLine({ icon, label, testId, children }: UserLineProps) {
  return (
    <div className={styles.userLine}>
      <dt className={styles.userTerm}>
        <i className={`${icon} ${styles.userIcon}`} aria-hidden />
        <span className={styles.srOnly}>{label}</span>
      </dt>
      <dd className={styles.userLineValue} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/** Data e hora do evento na mesma linha, como no protótipo. */
function renderDataHora(record: AuditRecord): string {
  if (!record.dataHora) return EMPTY_FIELD;

  const { date, time } = formatAuditDateTime(record.dataHora);

  return time ? `${date} ${time}` : date;
}

/**
 * Modal somente leitura dos detalhes de um registro de auditoria.
 *
 * Sem ações de edição ou exclusão e sem nenhuma escrita na abertura ou no
 * fechamento (RN004/RN005): a consulta do registro é a única chamada feita.
 */
export function AuditDetailDialog({ recordId, onClose, onReauthenticate }: AuditDetailDialogProps) {
  const { status, record, errorMessage, reload } = useAuditRecordDetail(recordId);

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={AUDIT_DETAIL_TITLE}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      ariaCloseIconLabel="Fechar"
      onHide={onClose}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="audit-detail-dialog"
    >
      {status === "loading" ? (
        <div className={styles.state} role="status" data-testid="audit-detail-loading">
          <i className={`pi pi-spinner pi-spin ${styles.stateIcon}`} aria-hidden />
          <p className={styles.stateText}>{AUDIT_DETAIL_LOADING_MESSAGE}</p>
        </div>
      ) : null}

      {/* Nenhuma falha exibe campo do registro: só a mensagem do cenário. */}
      {status !== "loading" && status !== "success" ? (
        <div className={styles.state} role="alert" data-testid={`audit-detail-${status}`}>
          <i
            className={`${status === "forbidden" || status === "sessionExpired" ? "pi pi-lock" : "pi pi-exclamation-triangle"} ${styles.stateIcon}`}
            aria-hidden
          />
          <p className={styles.stateText}>{errorMessage}</p>

          {status === "sessionExpired" ? (
            <Button
              type="button"
              className={styles.stateAction}
              label={AUDIT_DETAIL_REAUTH_LABEL}
              icon="pi pi-sign-in"
              onClick={onReauthenticate}
              data-testid="audit-detail-reauth"
            />
          ) : null}

          {status === "error" ? (
            <Button
              type="button"
              className={styles.stateAction}
              label={AUDIT_DETAIL_RETRY_LABEL}
              icon="pi pi-refresh"
              onClick={reload}
              data-testid="audit-detail-retry"
            />
          ) : null}
        </div>
      ) : null}

      {/* Todas as informações do registro reunidas em um card (protótipo). */}
      {record ? (
        <div className={styles.card} data-testid="audit-detail-fields">
          {/*
           * A ordem no DOM intercala as colunas para que o grid monte as linhas
           * do protótipo — Ação/Valor Anterior e Justificativa/Novo Valor —
           * inclusive quando um dos campos condicionais não é exibido.
           */}
          <dl className={styles.grid}>
            <DetailField label="Ação" testId="audit-detail-acao">
              <span className={styles.action}>{record.acao || EMPTY_FIELD}</span>
            </DetailField>

            {/* Só quando o evento troca um valor; no primeiro preenchimento, apenas o novo. */}
            {record.valorAnterior ? (
              <DetailField
                label="Valor Anterior"
                testId="audit-detail-valor-anterior"
                column="right"
              >
                {record.valorAnterior}
              </DetailField>
            ) : null}

            {/* Só quando o evento tem justificativa registrada (AC2). */}
            {record.justificativa ? (
              <DetailField label="Justificativa" testId="audit-detail-justificativa">
                {record.justificativa}
              </DetailField>
            ) : null}

            {record.valorNovo ? (
              <DetailField label="Novo Valor" testId="audit-detail-valor-novo" column="right">
                {record.valorNovo}
              </DetailField>
            ) : null}
          </dl>

          <hr className={styles.divider} />

          {/* Abaixo da divisória: o responsável à esquerda, o evento à direita. */}
          <div className={styles.footer}>
            <div className={styles.user} data-testid="audit-detail-usuario">
              <Avatar
                className={styles.avatar}
                shape="circle"
                {...(record.responsavel
                  ? { label: getInitials(record.responsavel) }
                  : { icon: "pi pi-user" })}
              />

              <div className={styles.userInfo}>
                <p className={styles.userName}>{record.responsavel || EMPTY_FIELD}</p>

                <dl className={styles.userLines}>
                  {record.responsavelEmail ? (
                    <UserLine
                      icon="pi pi-envelope"
                      label="E-mail"
                      testId="audit-detail-usuario-email"
                    >
                      {record.responsavelEmail}
                    </UserLine>
                  ) : null}

                  {record.responsavelTelefone ? (
                    <UserLine
                      icon="pi pi-phone"
                      label="Telefone"
                      testId="audit-detail-usuario-telefone"
                    >
                      {record.responsavelTelefone}
                    </UserLine>
                  ) : null}

                  {record.responsavelDetalhe ? (
                    <UserLine icon="pi pi-users" label="Cargo" testId="audit-detail-usuario-cargo">
                      {record.responsavelDetalhe}
                    </UserLine>
                  ) : null}
                </dl>
              </div>
            </div>

            <dl className={styles.meta}>
              <DetailField label="Data / Hora" testId="audit-detail-data-hora">
                <span className={styles.dateTime}>{renderDataHora(record)}</span>
              </DetailField>

              <DetailField label="Módulo" testId="audit-detail-modulo">
                {record.modulo ? (
                  <span className={styles.tag}>{formatAuditModule(record.modulo)}</span>
                ) : (
                  EMPTY_FIELD
                )}
              </DetailField>
            </dl>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
